import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Plus, Search, Download, Eye, Trash2, Edit2, Folder, FolderPlus, ChevronDown, ChevronRight, GripVertical, X, Settings, Upload, Cloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { documentsAPI } from '../services/api';
import { documentsFoldersMockData } from '../data/mockData';
import { useUserId } from '../context/UserAuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import GoogleDrivePanel from '../components/GoogleDrivePanel';

// Use runtime origin for API calls to ensure correct URL regardless of build-time env
const API_URL = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');
console.log('[Documents Page] Using runtime API_URL:', API_URL);

const DocumentsPageWithFolders = () => {
  const userId = useUserId();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedItemType, setDraggedItemType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Source tab — local documents vs Google Drive
  const [activeTab, setActiveTab] = useState('local'); // 'local' | 'drive'

  // Bulk upload state
  const bulkInputRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ uploaded: 0, total: 0, failed: 0 });
  
  // Category state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    color: 'bg-blue-600',
    textColor: 'text-white',
    icon: '📄'
  });
  
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    category: '',
    size: '',
    url: '',
    folder_id: ''
  });

  const [folderFormData, setFolderFormData] = useState({
    name: '',
    color: '#3b82f6',
    category: ''
  });

  // Color options for categories
  const colorOptions = [
    { value: 'bg-blue-600', label: 'Blue' },
    { value: 'bg-purple-600', label: 'Purple' },
    { value: 'bg-green-600', label: 'Green' },
    { value: 'bg-pink-600', label: 'Pink' },
    { value: 'bg-orange-600', label: 'Orange' },
    { value: 'bg-teal-600', label: 'Teal' },
    { value: 'bg-indigo-600', label: 'Indigo' },
    { value: 'bg-yellow-600', label: 'Yellow' },
    { value: 'bg-red-600', label: 'Red' },
    { value: 'bg-cyan-600', label: 'Cyan' }
  ];

  // Icon options for categories
  const iconOptions = ['📄', '📁', '🏠', '⚖️', '💰', '📣', '🏗️', '📊', '📋', '🔑', '📈', '✅', '📝', '💼', '🎯'];

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    if (!userId) {
      console.log('📂 No userId yet, skipping category fetch');
      return;
    }
    try {
      setCategoriesLoading(true);
      console.log('📂 Fetching categories for user:', userId);
      const response = await fetch(`${API_URL}/api/document-categories?user_id=${encodeURIComponent(userId)}`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      console.log('📂 Fetched categories:', data.categories?.length || 0);
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Set empty array on error to prevent infinite loading state
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchCategories();
      fetchData();
    }
  }, [userId, fetchCategories]);

  const fetchData = async () => {
    await Promise.all([fetchDocuments(), fetchFolders()]);
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      // USING MOCKUP DATA - Don't fetch from API
      console.log('📄 Using mockup documents data:', documentsFoldersMockData.documents.length);
      setDocuments(documentsFoldersMockData.documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      // USING MOCKUP DATA - Don't fetch from API
      console.log('📁 Using mockup folders data:', documentsFoldersMockData.folders.length);
      setFolders(documentsFoldersMockData.folders);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  // Bulk upload — accepts any number of files of any type (PDF, CSV, images, etc.)
  const handleBulkFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !userId) return;

    setBulkUploading(true);
    setBulkProgress({ uploaded: 0, total: files.length, failed: 0 });

    const fd = new FormData();
    fd.append('user_id', userId);
    fd.append('category', selectedCategory && selectedCategory !== 'all' ? selectedCategory : 'Properties');
    files.forEach(f => fd.append('files', f));

    try {
      const r = await fetch(`${API_URL}/api/documents-storage/upload-bulk`, {
        method: 'POST',
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Bulk upload failed');

      setBulkProgress({
        uploaded: data.uploaded || 0,
        failed: data.failed || 0,
        total: data.total || files.length,
      });

      // Optimistically prepend successful uploads so the user sees them immediately
      const newlyUploaded = (data.results || []).filter(r => r.ok).map(r => ({
        id: r.id,
        name: r.filename,
        type: r.file_type,
        category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : 'Properties',
        size: r.size,
        uploaded_at: new Date().toISOString(),
        download_url: `/api/documents-storage/download/${r.id}`,
      }));
      if (newlyUploaded.length) setDocuments(prev => [...newlyUploaded, ...prev]);

      if (data.failed > 0) {
        const failedNames = (data.results || []).filter(r => !r.ok).map(r => r.filename).join(', ');
        alert(`Uploaded ${data.uploaded}/${data.total}. Failed: ${failedNames}`);
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      alert(`Bulk upload failed: ${err.message}`);
    } finally {
      setBulkUploading(false);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
      // Auto-clear progress badge after a few seconds
      setTimeout(() => setBulkProgress({ uploaded: 0, total: 0, failed: 0 }), 4000);
    }
  };

  // Category CRUD operations
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/document-categories?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryFormData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create category');
      }
      setIsCategoryDialogOpen(false);
      setCategoryFormData({ name: '', color: 'bg-blue-600', textColor: 'text-white', icon: '📄' });
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      alert(`Failed to create category: ${error.message}`);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!userId || !editingCategory) return;
    try {
      const response = await fetch(`${API_URL}/api/document-categories/${encodeURIComponent(editingCategory.id)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryFormData)
      });
      if (!response.ok) throw new Error('Failed to update category');
      setIsCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryFormData({ name: '', color: 'bg-blue-600', textColor: 'text-white', icon: '📄' });
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (categoryId === 'all') {
      alert('Cannot delete the "All" category');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/document-categories/${encodeURIComponent(categoryId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete category');
      if (selectedCategory === categoryId) {
        setSelectedCategory('all');
      }
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  // Handle drag end for category reordering
  const handleCategoryDragEnd = async (result) => {
    if (!result.destination || !userId) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    // Get draggable categories (exclude 'all')
    const draggableCategories = categories.filter(c => c.id !== 'all');
    const allCategory = categories.find(c => c.id === 'all');
    
    // Reorder locally using standard array reorder
    const reordered = Array.from(draggableCategories);
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, removed);
    
    // Update local state immediately for better UX
    const newCategories = allCategory ? [allCategory, ...reordered] : reordered;
    setCategories(newCategories);
    
    // Get category IDs in new order (including 'all' at position 0)
    const categoryIds = newCategories.map(c => c.id);
    
    try {
      const response = await fetch(`${API_URL}/api/document-categories/reorder?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_ids: categoryIds })
      });
      if (!response.ok) throw new Error('Failed to reorder categories');
    } catch (error) {
      console.error('Error reordering categories:', error);
      // Revert on error
      fetchCategories();
    }
  };

  // Folder Management
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      const folderCategory = folderFormData.category || (selectedCategory === 'all' ? categories[0].id : selectedCategory);
      const response = await fetch('/api/folders/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: folderFormData.name,
          color: folderFormData.color,
          category: folderCategory,
          order: folders.filter(f => f.category === folderCategory).length
        })
      });
      
      if (!response.ok) throw new Error('Failed to create folder');
      
      setIsFolderDialogOpen(false);
      setFolderFormData({ name: '', color: '#3b82f6', category: '' });
      fetchFolders();
      alert('✅ Folder created successfully!');
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder');
    }
  };

  const handleUpdateFolder = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: folderFormData.name,
        color: folderFormData.color
      };
      
      // Only include category if it's changed
      if (folderFormData.category) {
        updateData.category = folderFormData.category;
      }
      
      const response = await fetch(`/api/folders/${editingFolder.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) throw new Error('Failed to update folder');
      
      setIsFolderDialogOpen(false);
      setEditingFolder(null);
      setFolderFormData({ name: '', color: '#3b82f6', category: '' });
      fetchFolders();
      alert('✅ Folder updated successfully!');
    } catch (error) {
      console.error('Error updating folder:', error);
      alert('Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Documents inside will be unfiled.')) return;
    
    try {
      const response = await fetch(`/api/folders/${folderId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete folder');
      
      fetchData();
      alert('✅ Folder deleted successfully!');
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder');
    }
  };

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Drag and Drop
  const handleDragStart = (e, item, type) => {
    setDraggedItem(item);
    setDraggedItemType(type);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnFolder = async (e, targetFolder) => {
    e.preventDefault();
    
    if (draggedItemType === 'document' && draggedItem) {
      // Move document to folder
      try {
        await documentsAPI.update(draggedItem.id, { folder_id: targetFolder.id });
        fetchDocuments();
        alert('✅ Document moved to folder!');
      } catch (error) {
        console.error('Error moving document:', error);
        alert('Failed to move document');
      }
    }
    
    setDraggedItem(null);
    setDraggedItemType(null);
  };

  const handleDropOnUnfiled = async (e) => {
    e.preventDefault();
    
    if (draggedItemType === 'document' && draggedItem && draggedItem.folder_id) {
      // Remove document from folder
      try {
        await documentsAPI.update(draggedItem.id, { folder_id: null });
        fetchDocuments();
        alert('✅ Document removed from folder!');
      } catch (error) {
        console.error('Error removing from folder:', error);
        alert('Failed to remove from folder');
      }
    }
    
    setDraggedItem(null);
    setDraggedItemType(null);
  };

  // Document Management (original functions)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let finalFormData = { ...formData };
      
      if (uploadedFile) {
        console.log('Uploading file:', uploadedFile.name);
        const uploadFormData = new FormData();
        uploadFormData.append('file', uploadedFile);
        
        const uploadResponse = await fetch('/api/uploads/media', {
          method: 'POST',
          body: uploadFormData
        });
        
        if (!uploadResponse.ok) throw new Error('File upload failed');
        
        const uploadData = await uploadResponse.json();
        finalFormData.url = uploadData.url;
      }
      
      if (!finalFormData.url) {
        alert('Please upload a file or enter a URL');
        return;
      }
      
      await documentsAPI.create(finalFormData);
      setIsDialogOpen(false);
      setUploadedFile(null);
      setFormData({ name: '', type: '', category: '', size: '', url: '', folder_id: '' });
      fetchDocuments();
      alert('✅ Document added successfully!');
    } catch (error) {
      console.error('Error creating document:', error);
      alert(`Failed to add document: ${error.message}`);
    }
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setFormData({
      name: document.name || '',
      type: document.type || '',
      category: document.category || '',
      size: document.size || '',
      url: document.url || '',
      folder_id: document.folder_id || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateDocument = async (e) => {
    e.preventDefault();
    try {
      let updateData = { ...formData };
      
      // If a new file is uploaded, upload it first and update URL
      if (uploadedFile) {
        console.log('Uploading new file for document update:', uploadedFile.name);
        const uploadFormData = new FormData();
        uploadFormData.append('file', uploadedFile);
        
        const uploadResponse = await fetch('/api/uploads/media', {
          method: 'POST',
          body: uploadFormData
        });
        
        if (!uploadResponse.ok) throw new Error('File upload failed');
        
        const uploadData = await uploadResponse.json();
        updateData.url = uploadData.url;
        console.log('✅ New file uploaded, URL updated');
      } else if (!updateData.url && editingDocument.url) {
        // If no new file uploaded, keep the original URL
        updateData.url = editingDocument.url;
      }
      
      await documentsAPI.update(editingDocument.id, updateData);
      setIsEditDialogOpen(false);
      setEditingDocument(null);
      setUploadedFile(null); // Clear uploaded file
      setFormData({ name: '', type: '', category: '', size: '', url: '', folder_id: '' });
      fetchDocuments();
      alert('✅ Document updated successfully!');
    } catch (error) {
      console.error('Error updating document:', error);
      alert(`Failed to update document: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentsAPI.delete(id);
        fetchDocuments();
        alert('✅ Document deleted successfully!');
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document');
      }
    }
  };

  // Filter logic
  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.type && doc.type.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const filteredFolders = folders.filter(folder => {
    const matchesCategory = selectedCategory === 'all' || folder.category === selectedCategory;
    const folderNameMatches = folder.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // If no search query, just check category
    if (!searchQuery) return matchesCategory;
    
    // Show folder if its name matches
    if (folderNameMatches) return matchesCategory;
    
    // Also show folder if it contains any matching documents
    const hasMatchingDocs = filteredDocuments.some(doc => doc.folder_id === folder.id);
    
    return matchesCategory && hasMatchingDocs;
  });

  const unfiledDocuments = filteredDocuments.filter(doc => !doc.folder_id);

  const getDocumentsInFolder = (folderId) => {
    return filteredDocuments.filter(doc => doc.folder_id === folderId);
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.color : 'bg-black';
  };

  return (
    <div className="h-screen p-8 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Documents</h1>
          <p className="text-white text-sm">All documents organized by category</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bulkProgress.total > 0 && (
            <span className="text-xs text-emerald-300 bg-emerald-900/40 border border-emerald-500/30 rounded-md px-2 py-1">
              {bulkProgress.uploaded}/{bulkProgress.total} uploaded
              {bulkProgress.failed > 0 && ` · ${bulkProgress.failed} failed`}
            </span>
          )}
          <input
            ref={bulkInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleBulkFiles}
            data-testid="documents-bulk-upload-input"
          />
          <button
            onClick={() => bulkInputRef.current?.click()}
            disabled={bulkUploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg transition-colors font-medium"
            data-testid="documents-bulk-upload-btn"
            title="Upload multiple files at once (PDF, CSV, images, etc.)"
          >
            <Upload className="w-5 h-5" />
            {bulkUploading ? 'Uploading…' : 'Bulk Upload'}
          </button>
          <button
            onClick={() => setIsDialogOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Source Tabs — Local vs Google Drive */}
      <div className="flex items-center gap-1 mb-4 bg-black/40 border border-white/10 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('local')}
          className={`px-3 sm:px-4 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 ${
            activeTab === 'local' ? 'bg-white text-black font-semibold' : 'text-gray-300 hover:text-white'
          }`}
          data-testid="documents-tab-local"
        >
          <Folder size={14} /> My Documents
        </button>
        <button
          onClick={() => setActiveTab('drive')}
          className={`px-3 sm:px-4 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 ${
            activeTab === 'drive' ? 'bg-white text-black font-semibold' : 'text-gray-300 hover:text-white'
          }`}
          data-testid="documents-tab-drive"
        >
          <Cloud size={14} /> Google Drive
        </button>
      </div>

      {activeTab === 'drive' ? (
        <div className="flex-1 overflow-auto pb-6">
          <GoogleDrivePanel userId={userId} />
        </div>
      ) : (
      <>
      {/* Category Tabs with Drag and Drop */}
      <div className="mb-6">
        <DragDropContext onDragEnd={handleCategoryDragEnd}>
          <Droppable droppableId="category-tabs" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="bg-transparent rounded-xl p-1 inline-flex gap-1 flex-wrap items-center"
              >
                {/* All Documents Tab (not draggable) */}
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
                    selectedCategory === 'all'
                      ? 'bg-white text-black'
                      : 'text-white hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="category-tab-all"
                >
                  All ({documents.length + folders.length})
                </button>
                
                {/* Draggable Category Tabs */}
                {categoriesLoading ? (
                  <span className="text-white/60 text-sm px-4">Loading categories...</span>
                ) : !userId ? (
                  <span className="text-white/60 text-sm px-4">Sign in to manage categories</span>
                ) : categories.filter(c => c.id !== 'all').length === 0 ? (
                  <span className="text-white/60 text-sm px-4">No categories yet</span>
                ) : (
                  categories.filter(c => c.id !== 'all').map((category, index) => {
                    const docCount = documents.filter(d => d.category === category.id).length;
                    const folderCount = folders.filter(f => f.category === category.id).length;
                    const totalCount = docCount + folderCount;
                    
                    return (
                      <Draggable key={category.id} draggableId={category.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`group relative flex items-center ${snapshot.isDragging ? 'z-50' : ''}`}
                          >
                            <button
                              onClick={() => setSelectedCategory(category.id)}
                              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap text-sm flex items-center gap-2 ${
                                selectedCategory === category.id
                                  ? 'bg-white text-black'
                                  : 'text-white hover:text-white hover:bg-white/10'
                              }`}
                              data-testid={`category-tab-${category.id}`}
                            >
                              <span {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                <GripVertical className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                              </span>
                              <span>{category.icon || '📁'}</span>
                              <span>{category.name} ({totalCount})</span>
                            </button>
                            
                            {/* Edit/Delete buttons on hover */}
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full hidden group-hover:flex items-center gap-1 pl-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingCategory(category);
                                  setCategoryFormData({
                                    name: category.name,
                                    color: category.color || 'bg-blue-600',
                                    textColor: category.textColor || 'text-white',
                                    icon: category.icon || '📄'
                                  });
                                  setIsCategoryDialogOpen(true);
                                }}
                                className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-white"
                                title="Edit category"
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              {!category.is_default && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCategory(category.id);
                                  }}
                                  className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-red-400"
                                  title="Delete category"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })
                )}
                {provided.placeholder}
                
                {/* Add Category Button */}
                <button
                  onClick={() => {
                    setEditingCategory(null);
                    setCategoryFormData({ name: '', color: 'bg-blue-600', textColor: 'text-white', icon: '📄' });
                    setIsCategoryDialogOpen(true);
                  }}
                  className="px-3 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all text-sm flex items-center gap-1"
                  data-testid="add-category-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Search Bar and New Folder Button */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white w-5 h-5" />
          <input
            type="text"
            placeholder="Search documents and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-transparent border border-transparent focus:border-blue-500 rounded-lg text-white placeholder-white focus:outline-none transition-colors"
          />
        </div>
        <button
          onClick={() => {
            setEditingFolder(null);
            setFolderFormData({ name: '', color: '#3b82f6', category: '' });
            setIsFolderDialogOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-transparent hover:bg-white/10 text-white rounded-lg transition-colors font-medium"
        >
          <FolderPlus className="w-5 h-5" />
          New Folder
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-white">Loading...</div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Folders */}
          {filteredFolders.map((folder) => {
            const folderDocs = getDocumentsInFolder(folder.id);
            const isExpanded = expandedFolders.has(folder.id);
            
            return (
              <div
                key={folder.id}
                className="relative rounded-2xl border border-white/60 overflow-hidden hover:border-white transition-all bg-transparent"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropOnFolder(e, folder)}
              >
                {/* Removed gradient overlay for clean transparent look */}
                
                {/* Folder Header */}
                <div className="relative flex items-center gap-3 p-3 cursor-pointer" onClick={() => toggleFolder(folder.id)}>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-white flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-white flex-shrink-0" />
                  )}
                  
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="relative w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-transparent">
                      <Folder className="w-4 h-4 relative z-10 text-white" />
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <h3 className="text-white font-medium text-sm">{folder.name}</h3>
                        {folder.category && (() => {
                          const category = categories.find(c => c.id === folder.category);
                          return category ? (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-transparent border border-white/60 text-white">
                              {category.name}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      <p className="text-xs text-white/60">{folderDocs.length} documents</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingFolder(folder);
                        setFolderFormData({ name: folder.name, color: folder.color, category: folder.category || '' });
                        setIsFolderDialogOpen(true);
                      }}
                      className="p-2 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                      className="p-2 hover:bg-white/5 rounded-lg text-white hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Folder Contents */}
                {isExpanded && (
                  <div className="relative px-5 pb-5 space-y-3 border-t border-white/60">
                    {folderDocs.length === 0 ? (
                      <p className="text-white text-sm italic py-8 text-center">
                        Drag and drop documents here to organize them in this folder
                      </p>
                    ) : (
                      <div className="pt-3 space-y-3">
                        {folderDocs.map((doc) => (
                          <DocumentCard
                            key={doc.id}
                            doc={doc}
                            categories={categories}
                            onDragStart={handleDragStart}
                            onEdit={handleEditDocument}
                            onDelete={handleDelete}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Unfiled Documents */}
          {unfiledDocuments.length > 0 && (
            <div onDragOver={handleDragOver} onDrop={handleDropOnUnfiled}>
              {filteredFolders.length > 0 && (
                <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2 px-1">
                  <FileText className="w-4 h-4" />
                  Unfiled Documents
                </h3>
              )}
              <div className="space-y-2">
                {unfiledDocuments.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    doc={doc}
                    categories={categories}
                    onDragStart={handleDragStart}
                    onEdit={handleEditDocument}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {filteredDocuments.length === 0 && (
            <div className="text-center py-16 bg-transparent rounded-xl border border-[#2a3752]">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-white text-lg mb-2">No documents yet</p>
              <p className="text-white text-sm">Click "Upload Document" to add your first file</p>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Add Document Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-black border border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Upload Document</DialogTitle>
            <DialogDescription className="text-white">
              Add document information to your library
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Document Name *</label>
              <input
                type="text"
                placeholder="e.g., Contract_Agreement.pdf"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
              <input
                type="text"
                placeholder="e.g., Contract, Report, Presentation"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select category</option>
                {categories.filter(cat => cat.id !== 'all').map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Size</label>
              <input
                type="text"
                placeholder="e.g., 2.4 MB"
                value={formData.size}
                onChange={(e) => setFormData({...formData, size: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
              <input
                type="url"
                placeholder="https://"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Or Upload File</label>
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-cyan-600 text-white rounded-lg transition-colors">
                    <span className="text-sm font-medium">Choose File</span>
                  </div>
                  <input
                    type="file"
                    onChange={(e) => setUploadedFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
                <span className="text-sm text-white">
                  {uploadedFile ? uploadedFile.name : 'No file chosen'}
                </span>
              </div>
              <p className="text-xs text-white mt-2">Upload a document file or enter URL above</p>
            </div>
            
            <DialogFooter className="flex gap-3 mt-6">
              <Button 
                type="button" 
                onClick={() => setIsDialogOpen(false)}
                className="flex-1 bg-transparent hover:bg-white/5 text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Add Document
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-black border border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Edit Document</DialogTitle>
            <DialogDescription className="text-white">
              Update document information in your library
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDocument} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Document Name *</label>
              <input
                type="text"
                placeholder="e.g., Contract_Agreement.pdf"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
              <input
                type="text"
                placeholder="e.g., Contract, Report, Presentation"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select category</option>
                {categories.filter(cat => cat.id !== 'all').map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Size</label>
              <input
                type="text"
                placeholder="e.g., 2.4 MB"
                value={formData.size}
                onChange={(e) => setFormData({...formData, size: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Replace Document File (Optional)</label>
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer">
                  <div className="px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white hover:bg-white/10 transition-colors flex items-center justify-center">
                    <span className="text-sm">Choose New File</span>
                  </div>
                  <input
                    type="file"
                    onChange={(e) => setUploadedFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
                <span className="text-sm text-white flex-1">
                  {uploadedFile ? uploadedFile.name : 'No new file selected'}
                </span>
              </div>
              <p className="text-xs text-white mt-2">Upload a new file to replace the existing document</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Current File URL (Read-only)</label>
              <input
                type="text"
                value={formData.url}
                readOnly
                className="w-full px-4 py-2.5 bg-black border border-white/60 rounded-lg text-white cursor-not-allowed text-xs"
                placeholder="Document file URL"
              />
            </div>
            
            <DialogFooter className="flex gap-3 mt-6">
              <Button 
                type="button" 
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setUploadedFile(null); // Clear uploaded file on cancel
                }}
                className="flex-1 bg-transparent hover:bg-white/5 text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Update Document
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Folder Dialog */}
      <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
        <DialogContent className="bg-black border border-white/60 text-white">
          <DialogHeader>
            <DialogTitle>{editingFolder ? 'Edit Folder' : 'Create New Folder'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingFolder ? handleUpdateFolder : handleCreateFolder} className="space-y-4">
            <div>
              <label className="block text-sm text-white mb-2">Folder Name *</label>
              <input
                type="text"
                placeholder="Folder Name"
                value={folderFormData.name}
                onChange={(e) => setFolderFormData({...folderFormData, name: e.target.value})}
                className="w-full px-3 py-2 bg-white/5 border border-white/60 rounded-lg text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm text-white mb-2">Category *</label>
              <select
                value={folderFormData.category || (selectedCategory === 'all' ? '' : selectedCategory)}
                onChange={(e) => setFolderFormData({...folderFormData, category: e.target.value})}
                className="w-full px-3 py-2 bg-white/5 border border-white/60 rounded-lg text-white"
                required
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-white mb-2">Folder Color</label>
              <input
                type="color"
                value={folderFormData.color}
                onChange={(e) => setFolderFormData({...folderFormData, color: e.target.value})}
                className="w-full h-10 bg-white/5 border border-white/60 rounded-lg"
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-black hover:bg-white/10">
                {editingFolder ? 'Update Folder' : 'Create Folder'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent className="bg-black border border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              {editingCategory ? 'Edit Category' : 'Create New Category'}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {editingCategory ? 'Update category details' : 'Add a new category to organize your documents'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category Name *</label>
              <input
                type="text"
                placeholder="e.g., Contracts, Reports"
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                className="w-full px-4 py-2.5 bg-transparent border border-white/60 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                data-testid="category-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Icon</label>
              <div className="flex flex-wrap gap-2">
                {iconOptions.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setCategoryFormData({...categoryFormData, icon})}
                    className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                      categoryFormData.icon === icon
                        ? 'bg-white/20 border-2 border-white'
                        : 'bg-white/5 border border-white/20 hover:bg-white/10'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Color</label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setCategoryFormData({...categoryFormData, color: color.value})}
                    className={`w-10 h-10 rounded-lg ${color.value} transition-all ${
                      categoryFormData.color === color.value
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-black'
                        : 'hover:opacity-80'
                    }`}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            
            <DialogFooter className="flex gap-3 mt-6">
              <Button 
                type="button" 
                onClick={() => {
                  setIsCategoryDialogOpen(false);
                  setEditingCategory(null);
                }}
                className="flex-1 bg-transparent hover:bg-white/5 text-white border border-white/30"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="category-submit-btn"
              >
                {editingCategory ? 'Update Category' : 'Create Category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Document Card Component
const DocumentCard = ({ doc, categories, onDragStart, onEdit, onDelete }) => {
  const category = categories?.find(c => c.id === doc.category);
  
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, doc, 'document')}
      className="relative flex items-center gap-3 p-3 rounded-2xl border border-white/60 hover:border-white transition-all cursor-move group overflow-hidden bg-transparent"
    >
      {/* Clean transparent design - no gradient overlay */}
      
      {/* File Icon - simplified */}
      <div className="relative w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-transparent">
        <FileText className="w-5 h-5 text-white relative z-10" />
      </div>
      
      {/* Document Info */}
      <div className="flex-1 min-w-0 relative z-10">
        <h4 className="text-white font-medium text-sm mb-1.5 break-words leading-snug">{doc.name}</h4>
        <div className="flex items-center gap-2 flex-wrap">
          {category && (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-transparent border border-white/60 text-white">
              {category.name}
            </span>
          )}
          {doc.type && (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-transparent border border-white/60 text-white">
              {doc.type}
            </span>
          )}
        </div>
        {(doc.size || doc.uploaded_at) && (
          <div className="flex items-center gap-3 mt-2">
            {doc.size && (
              <span className="text-xs text-white">Size: {doc.size}</span>
            )}
            {doc.uploaded_at && (
              <span className="text-xs text-white">
                Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 relative z-10">
        {doc.url && (
          <>
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
              title="View"
            >
              <Eye className="w-4 h-4" />
            </a>
            <a
              href={doc.url}
              download
              onClick={(e) => e.stopPropagation()}
              className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </a>
          </>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(doc);
          }}
          className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-white transition-colors"
          title="Edit"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(doc.id);
          }}
          className="p-2.5 hover:bg-white/5 rounded-lg text-white hover:text-red-400 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <div className="p-2.5 text-white cursor-move">
          <GripVertical className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

export default DocumentsPageWithFolders;
